
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoRecorder from '../components/VideoRecorder';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Info, Lock } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { generateVideoSummary } from '../utils/videoAnalysis';

const VideoIntro = () => {
  const navigate = useNavigate();
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [cheatingDetected, setCheatingDetected] = useState(false);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!authToken || userType !== 'candidate') {
      toast.error('Please log in as a candidate to continue');
      navigate('/login');
      return;
    }
    
    // Check if user came from assessment
    const assessmentData = sessionStorage.getItem('assessmentData');
    if (!assessmentData) {
      toast.error('You need to complete the assessment before recording your video');
      setIsAuthorized(false);
      
      // Redirect after showing error
      setTimeout(() => {
        navigate('/assessment');
      }, 2000);
      return;
    }
    
    setIsAuthorized(true);
    
    // Check for cheating flags from assessment
    try {
      const parsed = JSON.parse(assessmentData);
      if (parsed.cheatingFlags) {
        const { tabSwitched, blurred, aiDetected, timeExpired } = parsed.cheatingFlags;
        const hasCheated = tabSwitched || blurred || aiDetected || timeExpired;
        
        setCheatingDetected(hasCheated);
        
        if (hasCheated) {
          toast.warning('We detected potential issues during your assessment. This may affect your application.', {
            duration: 5000,
            position: 'bottom-center'
          });
        }
      }
    } catch (e) {
      console.error('Error parsing assessment data:', e);
    }
  }, [navigate]);

  const handleRecordingComplete = async (url: string) => {
    setRecordingUrl(url);
    setIsComplete(true);
    
    // Save video URL
    sessionStorage.setItem('videoRecordingUrl', url);
    
    // Generate AI summary of the video
    setIsProcessingVideo(true);
    try {
      const videoSummary = await generateVideoSummary(url);
      
      // Save the summary in session storage
      sessionStorage.setItem('videoSummary', JSON.stringify(videoSummary));
      
      toast.success('Video analysis complete');
    } catch (error) {
      console.error('Error analyzing video:', error);
      toast.error('Failed to analyze video');
    } finally {
      setIsProcessingVideo(false);
    }
  };

  const handleSubmit = () => {
    if (!isComplete) {
      toast.error('Please complete the video recording first');
      return;
    }

    toast.success('Application submitted successfully!');
    
    // Record completion in session storage to prevent re-accessing this step
    sessionStorage.setItem('applicationCompleted', 'true');
    
    // Redirect to confirmation page or homepage
    setTimeout(() => {
      navigate('/application-status');
    }, 1500);
  };

  const handleBack = () => {
    navigate('/assessment');
  };

  if (isAuthorized === false) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
              <Lock className="h-8 w-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-apple-text mb-4">
              Access Restricted
            </h1>
            <p className="text-apple-text-secondary mb-6">
              You need to complete the assessment before you can record your video introduction.
              Redirecting you to the assessment page...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apple-blue mx-auto"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (isAuthorized === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apple-blue"></div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Progress indicator */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-apple-text-secondary">
                  Step 4 of 4
                </span>
                <span className="text-sm font-medium text-apple-blue">
                  Video Introduction
                </span>
              </div>
              <div className="bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-apple-blue h-full transition-all duration-500 ease-apple"
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>

            {cheatingDetected && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="text-amber-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h3 className="font-medium text-amber-800 mb-1">Assessment Integrity Notice</h3>
                    <p className="text-sm text-amber-700">
                      We detected potential integrity issues during your assessment. This may affect our evaluation of your application.
                      Please ensure you follow all instructions during the application process.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Form card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-semibold text-apple-text mb-2">
                  Record Your Introduction
                </h1>
                <p className="text-apple-text-secondary mb-4">
                  Please record a short video introducing yourself and talking about your core experience. This helps us get to know you better.
                </p>

                <Alert className="mb-6 bg-blue-50 border-blue-100">
                  <Info className="h-4 w-4 text-blue-500" />
                  <AlertTitle className="text-blue-700">Important information</AlertTitle>
                  <AlertDescription className="text-blue-600 text-sm">
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>Your video should be no longer than <strong>5 minutes</strong>.</li>
                      <li>Introduce yourself and highlight your most relevant experience for this position.</li>
                      <li>Discuss specific achievements and skills that make you a good fit.</li>
                      <li>You can re-record your video as many times as you need before submitting.</li>
                      <li>The video will be viewed only by our hiring team and will not be shared externally.</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <VideoRecorder onRecordingComplete={handleRecordingComplete} maxRecordingTime={300} />

                {isComplete && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-lg flex items-center">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-medium text-green-800">Video recorded successfully!</h3>
                      <p className="text-sm text-green-700">
                        Your introduction has been saved. You can now submit your application or re-record if needed.
                      </p>
                    </div>
                  </div>
                )}

                {isProcessingVideo && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-center">
                      <div className="mr-3 h-4 w-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                      <p className="text-blue-700">Analyzing your video introduction...</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="order-2 sm:order-1 apple-btn-secondary flex items-center justify-center"
                  >
                    <ArrowLeft className="mr-2" size={18} />
                    <span>Back</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!isComplete || isProcessingVideo}
                    className={`order-1 sm:order-2 apple-btn flex items-center justify-center ${
                      (!isComplete || isProcessingVideo) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <span>Submit Application</span>
                    <ArrowRight className="ml-2" size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VideoIntro;
